/**
 * audioAnalyserBus — a MODULE-SCOPE Web Audio FFT bus feeding the audio-reactive splat effect.
 *
 * Why module scope (same reasoning as crossSceneAudio): `createMediaElementSource(el)` may be
 * called ONCE per element, ever, and permanently reroutes that element's output through the
 * AudioContext that created the node. The crossSceneAudio bed element survives viewer
 * recreation (portal teleports), so the context and the cached source nodes must survive too.
 * Consequences honoured here:
 *  - the AudioContext is never closed;
 *  - every element source stays connected to ctx.destination forever (disconnecting it would
 *    silence the bed for the rest of the page's life);
 *  - viewer teardown only detaches the analyser fan-in and the mic (releaseViewer()).
 *
 * PlayCanvas sound slots use the engine's own AudioContext, so their GainNodes cannot connect to
 * the media-element context above. Non-positional slots share one global analyser side-chain per
 * engine context, while every localized slot gets its own analyser so its FFT remains attributable
 * to that emitter. Every side-chain is instance gain -> analyser -> zero-gain sink -> destination;
 * the original audible route is never disconnected or replaced.
 *
 * The mic source connects to the analyser ONLY — never to destination (feedback loop).
 */

export interface AudioBands {
    /** 20–160 Hz, smoothed 0..1 */
    bass: number;
    /** 160–2000 Hz, smoothed 0..1 */
    mid: number;
    /** 2–8 kHz, smoothed 0..1 */
    treble: number;
    /** Weighted overall level, smoothed 0..1 */
    level: number;
}

export interface LocalizedAudioSample {
    node: AudioNode;
    bands: AudioBands;
    /** Unnormalized weighted level, used to choose between localized emitters. */
    rawLevel: number;
}

export interface AudioAnalyserSample {
    global: AudioBands;
    localized: LocalizedAudioSample[];
}

export type MicrophoneInputState = 'idle' | 'requesting' | 'active' | 'denied' | 'unavailable';

const FFT_SIZE = 1024;
const GESTURE_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;

// Band edges in Hz. Bin i covers i * sampleRate / fftSize Hz.
const BASS_LO = 20, BASS_HI = 160;
const MID_HI = 2000;
const TREBLE_HI = 8000;

// Auto-gain: normalize each band by a slowly-decaying running max so quietly
// mastered tracks still animate. Decays over ~8 s; floor prevents noise blow-up.
const GAIN_DECAY_PER_SEC = 0.12;
const GAIN_FLOOR = 0.12;

interface AudioContextWindow extends Window {
    AudioContext: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
}

function createFrequencyData(length: number) {
    return new Uint8Array(length);
}

type FrequencyData = ReturnType<typeof createFrequencyData>;

let ctx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let freqData: FrequencyData | null = null;
let gestureResumeArmed = false;

const elementSources = new WeakMap<HTMLAudioElement, MediaElementAudioSourceNode>();
const attachedToAnalyser = new Set<MediaElementAudioSourceNode>();

interface PlayCanvasAnalyserGraph {
    context: AudioContext;
    analyser: AnalyserNode;
    silentSink: GainNode;
    freqData: FrequencyData;
    nodes: Set<AudioNode>;
}

interface BandState {
    smoothed: AudioBands;
    runningMax: AudioBands;
}

interface PlayCanvasAttachment {
    context: AudioContext;
    localized: boolean;
    graph: PlayCanvasAnalyserGraph;
    /** Present only for localized nodes; global nodes share globalBandState. */
    state: BandState | null;
}

const globalPlayCanvasGraphs = new Map<AudioContext, PlayCanvasAnalyserGraph>();
const attachedPlayCanvasNodes = new Map<AudioNode, PlayCanvasAttachment>();

let micStream: MediaStream | null = null;
let micSource: MediaStreamAudioSourceNode | null = null;
let micRequestGeneration = 0;
let micPendingGeneration: number | null = null;
let micState: MicrophoneInputState = 'idle';
const micStateListeners = new Set<(state: MicrophoneInputState) => void>();

function hasMicrophoneSupport(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    if (window.isSecureContext === false) return false;
    const browserWindow: AudioContextWindow = window;
    const Ctor = browserWindow.AudioContext ?? browserWindow.webkitAudioContext;
    return !!Ctor && !!navigator.mediaDevices?.getUserMedia;
}

function setMicState(next: MicrophoneInputState): void {
    if (micState === next) return;
    micState = next;
    micStateListeners.forEach(listener => listener(next));
}

function classifyMicrophoneError(error: object): MicrophoneInputState {
    return Reflect.get(error, 'name') === 'NotAllowedError'
        ? 'denied'
        : 'unavailable';
}

function createBandState(): BandState {
    return {
        smoothed: { bass: 0, mid: 0, treble: 0, level: 0 },
        runningMax: { bass: GAIN_FLOOR, mid: GAIN_FLOOR, treble: GAIN_FLOOR, level: GAIN_FLOOR },
    };
}

const globalBandState = createBandState();

function armGestureResume(): void {
    if (gestureResumeArmed || typeof window === 'undefined') return;
    gestureResumeArmed = true;
    const handler = (): void => {
        GESTURE_EVENTS.forEach(ev => window.removeEventListener(ev, handler));
        gestureResumeArmed = false;
        ctx?.resume().catch(() => { /* still blocked; re-armed on next ensure */ });
    };
    GESTURE_EVENTS.forEach(ev => window.addEventListener(ev, handler, { passive: true }));
}

function ensureContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!ctx) {
        const browserWindow: AudioContextWindow = window;
        const Ctor = browserWindow.AudioContext ?? browserWindow.webkitAudioContext;
        if (!Ctor) return null;
        ctx = new Ctor();
    }
    if (ctx.state === 'suspended') armGestureResume();
    return ctx;
}

function ensureAnalyser(c: AudioContext): AnalyserNode {
    if (!analyser) {
        analyser = c.createAnalyser();
        analyser.fftSize = FFT_SIZE;
        // We do our own attack/release smoothing per band; raw frames in.
        analyser.smoothingTimeConstant = 0;
        freqData = createFrequencyData(analyser.frequencyBinCount);
    }
    return analyser;
}

function createPlayCanvasGraph(context: AudioContext): PlayCanvasAnalyserGraph {
    const externalAnalyser = context.createAnalyser();
    externalAnalyser.fftSize = FFT_SIZE;
    externalAnalyser.smoothingTimeConstant = 0;
    // Keep the analysis branch in the rendered graph without doubling audible output.
    const silentSink = context.createGain();
    silentSink.gain.value = 0;
    externalAnalyser.connect(silentSink);
    silentSink.connect(context.destination);

    const graph: PlayCanvasAnalyserGraph = {
        context,
        analyser: externalAnalyser,
        silentSink,
        freqData: createFrequencyData(externalAnalyser.frequencyBinCount),
        nodes: new Set(),
    };
    return graph;
}

function ensureGlobalPlayCanvasGraph(context: AudioContext): PlayCanvasAnalyserGraph {
    const existing = globalPlayCanvasGraphs.get(context);
    if (existing) return existing;

    const graph = createPlayCanvasGraph(context);
    globalPlayCanvasGraphs.set(context, graph);
    return graph;
}

function cleanupPlayCanvasGraph(graph: PlayCanvasAnalyserGraph): void {
    try { graph.analyser.disconnect(graph.silentSink); } catch { /* already disconnected */ }
    try { graph.silentSink.disconnect(graph.context.destination); } catch { /* already disconnected */ }
    graph.nodes.clear();
    if (globalPlayCanvasGraphs.get(graph.context) === graph) {
        globalPlayCanvasGraphs.delete(graph.context);
    }
}

function detachPlayCanvasAttachment(node: AudioNode, attachment: PlayCanvasAttachment): void {
    // Disconnect only the analysis tap. Calling node.disconnect() with no destination would also
    // remove PlayCanvas's audible route.
    try { node.disconnect(attachment.graph.analyser); } catch { /* stopped/destroyed instance */ }
    attachment.graph.nodes.delete(node);
    attachedPlayCanvasNodes.delete(node);
    if (attachment.graph.nodes.size === 0) cleanupPlayCanvasGraph(attachment.graph);
}

/** Average byte magnitude (0..1) over [loHz, hiHz). */
function bandAverage(data: Uint8Array, sampleRate: number, loHz: number, hiHz: number): number {
    const hzPerBin = sampleRate / FFT_SIZE;
    const lo = Math.max(0, Math.floor(loHz / hzPerBin));
    const hi = Math.min(data.length - 1, Math.ceil(hiHz / hzPerBin));
    if (hi <= lo) return 0;
    let sum = 0;
    for (let i = lo; i <= hi; i++) sum += data[i];
    return sum / ((hi - lo + 1) * 255);
}

function smoothBand(cur: number, target: number, dt: number, attack: number, release: number): number {
    const tau = target > cur ? Math.max(0.005, attack) : Math.max(0.005, release);
    const k = 1 - Math.exp(-dt / tau);
    return cur + (target - cur) * k;
}

function autoGain(state: BandState, key: keyof AudioBands, raw: number, dt: number): number {
    const currentMax = state.runningMax[key];
    const decayed = Math.max(GAIN_FLOOR, currentMax - GAIN_DECAY_PER_SEC * dt * currentMax);
    state.runningMax[key] = Math.max(decayed, raw);
    return Math.min(1, raw / state.runningMax[key]);
}

function readRawBands(
    context: AudioContext,
    sourceAnalyser: AnalyserNode,
    data: Uint8Array<ArrayBuffer>,
): AudioBands {
    if (context.state !== 'running') return { bass: 0, mid: 0, treble: 0, level: 0 };

    sourceAnalyser.getByteFrequencyData(data);
    const bass = bandAverage(data, context.sampleRate, BASS_LO, BASS_HI);
    const mid = bandAverage(data, context.sampleRate, BASS_HI, MID_HI);
    const treble = bandAverage(data, context.sampleRate, MID_HI, TREBLE_HI);
    return { bass, mid, treble, level: bass * 0.5 + mid * 0.35 + treble * 0.15 };
}

function mergeRawBands(target: AudioBands, source: AudioBands): void {
    target.bass = Math.max(target.bass, source.bass);
    target.mid = Math.max(target.mid, source.mid);
    target.treble = Math.max(target.treble, source.treble);
    target.level = Math.max(target.level, source.level);
}

function updateBandState(
    state: BandState,
    raw: AudioBands,
    dt: number,
    attack: number,
    release: number,
): AudioBands {
    // One auto-gain pass per band, after all raw inputs for this scope have been merged.
    const normalized: AudioBands = {
        bass: autoGain(state, 'bass', raw.bass, dt),
        mid: autoGain(state, 'mid', raw.mid, dt),
        treble: autoGain(state, 'treble', raw.treble, dt),
        level: autoGain(state, 'level', raw.level, dt),
    };
    state.smoothed.bass = smoothBand(state.smoothed.bass, normalized.bass, dt, attack, release);
    state.smoothed.mid = smoothBand(state.smoothed.mid, normalized.mid, dt, attack, release);
    state.smoothed.treble = smoothBand(state.smoothed.treble, normalized.treble, dt, attack, release);
    state.smoothed.level = smoothBand(state.smoothed.level, normalized.level, dt, attack, release);
    return { ...state.smoothed };
}

function resetBandState(state: BandState): void {
    state.smoothed.bass = 0;
    state.smoothed.mid = 0;
    state.smoothed.treble = 0;
    state.smoothed.level = 0;
    state.runningMax.bass = GAIN_FLOOR;
    state.runningMax.mid = GAIN_FLOOR;
    state.runningMax.treble = GAIN_FLOOR;
    state.runningMax.level = GAIN_FLOOR;
}

export const audioAnalyserBus = {
    /**
     * Route an audio element into the analyser. Idempotent — safe to call every frame.
     * The element keeps playing audibly: its (cached, once-ever) source node is connected
     * back to ctx.destination the moment it is created.
     */
    attachElement(el: HTMLAudioElement): void {
        const c = ensureContext();
        if (!c) return;
        const a = ensureAnalyser(c);
        let src = elementSources.get(el);
        if (!src) {
            try {
                src = c.createMediaElementSource(el);
            } catch {
                // Element already claimed by another AudioContext — leave its playback alone.
                return;
            }
            elementSources.set(el, src);
            src.connect(c.destination); // NEVER disconnected — see module docblock
        }
        if (!attachedToAnalyser.has(src)) {
            src.connect(a);
            attachedToAnalyser.add(src);
        }
    },

    /** Detach all media elements from analysis without touching their audible destinations. */
    detachElements(): void {
        if (analyser) {
            attachedToAnalyser.forEach(src => {
                try { src.disconnect(analyser as AnalyserNode); } catch { /* already detached */ }
            });
        }
        attachedToAnalyser.clear();
    },

    /** Tap a live PlayCanvas SoundInstance gain node without changing its audible routing. */
    attachPlayCanvasNode(node: AudioNode, context: AudioContext, localized: boolean): void {
        if (!node || !context) return;
        const current = attachedPlayCanvasNodes.get(node);
        if (current && current.context === context && current.localized === localized) return;

        let graph: PlayCanvasAnalyserGraph | null = null;
        try {
            graph = localized ? createPlayCanvasGraph(context) : ensureGlobalPlayCanvasGraph(context);
            // Connect the new classification before removing the old one. A mismatched context can
            // throw here; in that case the existing, valid analysis attachment remains intact.
            node.connect(graph.analyser);
        } catch {
            // A node can only connect within the AudioContext that created it. If an engine/backend
            // reports a mismatched node, leave playback untouched and simply skip its analysis.
            if (graph?.nodes.size === 0) cleanupPlayCanvasGraph(graph);
            return;
        }

        if (current) detachPlayCanvasAttachment(node, current);
        graph.nodes.add(node);
        attachedPlayCanvasNodes.set(node, {
            context,
            localized,
            graph,
            state: localized ? createBandState() : null,
        });
    },

    /** Remove only a node's analysis tap, retaining every pre-existing audible connection. */
    detachPlayCanvasNode(node: AudioNode): void {
        const attachment = attachedPlayCanvasNodes.get(node);
        if (attachment) detachPlayCanvasAttachment(node, attachment);
    },

    /**
     * Start microphone analysis. Call from (or shortly after) a user gesture so the
     * permission prompt isn't suppressed. Resolves false when denied/unavailable.
     */
    async enableMic(): Promise<boolean> {
        if (micSource) return true;
        if (micPendingGeneration !== null) return false;
        if (!hasMicrophoneSupport()) {
            setMicState('unavailable');
            return false;
        }
        const c = ensureContext();
        if (!c) {
            setMicState('unavailable');
            return false;
        }
        const requestGeneration = ++micRequestGeneration;
        micPendingGeneration = requestGeneration;
        setMicState('requesting');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
            });
            // Permission prompts cannot be cancelled. If the viewer was destroyed or switched
            // away from microphone mode while the prompt was open, discard the late stream.
            if (requestGeneration !== micRequestGeneration) {
                stream.getTracks().forEach(track => track.stop());
                return false;
            }
            micStream = stream;
            micSource = c.createMediaStreamSource(stream);
            micSource.connect(ensureAnalyser(c)); // analyser only — never destination
            stream.getTracks().forEach(track => {
                track.addEventListener?.('ended', () => {
                    if (micStream !== stream) return;
                    try { micSource?.disconnect(); } catch { /* already detached */ }
                    micSource = null;
                    micStream = null;
                    setMicState('unavailable');
                }, { once: true });
            });
            setMicState('active');
            return true;
        } catch (error) {
            if (requestGeneration === micRequestGeneration) {
                setMicState(typeof error === 'object' && error !== null
                    ? classifyMicrophoneError(error)
                    : 'unavailable');
            }
            return false;
        } finally {
            if (micPendingGeneration === requestGeneration) micPendingGeneration = null;
        }
    },

    disableMic(): void {
        // Invalidate an unresolved getUserMedia request. Its eventual stream is stopped in
        // enableMic() rather than being installed into a destroyed or reconfigured viewer.
        micRequestGeneration++;
        micPendingGeneration = null;
        if (micSource) {
            try { micSource.disconnect(); } catch { /* ignore */ }
            micSource = null;
        }
        if (micStream) {
            micStream.getTracks().forEach(t => t.stop());
            micStream = null;
        }
        setMicState(hasMicrophoneSupport() ? 'idle' : 'unavailable');
    },

    isMicActive(): boolean { return !!micSource; },

    getMicState(): MicrophoneInputState {
        return micState === 'idle' && !hasMicrophoneSupport() ? 'unavailable' : micState;
    },

    subscribeMicState(listener: (state: MicrophoneInputState) => void): () => void {
        micStateListeners.add(listener);
        listener(this.getMicState());
        return () => micStateListeners.delete(listener);
    },

    /**
     * Per-frame band sample with attack/release smoothing and slow auto-gain.
     * Returns decaying-to-zero values when the context is suspended or nothing plays.
     */
    sample(dt: number, attack: number, release: number): AudioAnalyserSample {
        const globalRaw: AudioBands = { bass: 0, mid: 0, treble: 0, level: 0 };
        if (ctx && analyser && freqData && ctx.state === 'running') {
            mergeRawBands(globalRaw, readRawBands(ctx, analyser, freqData));
        }

        // PlayCanvas global slots live in separate contexts. Merge raw bands by maximum first,
        // then normalize exactly once so source count and iteration order cannot alter auto-gain.
        globalPlayCanvasGraphs.forEach((graph) => {
            mergeRawBands(globalRaw, readRawBands(graph.context, graph.analyser, graph.freqData));
        });

        const localized: LocalizedAudioSample[] = [];
        attachedPlayCanvasNodes.forEach((attachment, node) => {
            if (!attachment.localized || !attachment.state) return;
            const raw = readRawBands(attachment.context, attachment.graph.analyser, attachment.graph.freqData);
            localized.push({
                node,
                bands: updateBandState(attachment.state, raw, dt, attack, release),
                rawLevel: raw.level,
            });
        });

        return {
            global: updateBandState(globalBandState, globalRaw, dt, attack, release),
            localized,
        };
    },

    /** Resume the context (call from user-gesture paths, e.g. the "Tap for audio" overlay). */
    resume(): void {
        const contexts = new Set<AudioContext>();
        if (ctx) contexts.add(ctx);
        globalPlayCanvasGraphs.forEach(graph => contexts.add(graph.context));
        attachedPlayCanvasNodes.forEach(attachment => contexts.add(attachment.context));
        contexts.forEach(context => context.resume().catch(() => { /* ignore */ }));
    },

    getContext(): AudioContext | null { return ctx; },

    /**
     * Viewer teardown: detach analyser fan-in + mic. The context and every
     * element→destination connection stay alive (irreversible reroute — see docblock).
     */
    releaseViewer(): void {
        this.detachElements();
        Array.from(attachedPlayCanvasNodes.entries()).forEach(([node, attachment]) => {
            detachPlayCanvasAttachment(node, attachment);
        });
        // Defensive cleanup for a graph whose node failed independently before teardown.
        globalPlayCanvasGraphs.forEach(graph => cleanupPlayCanvasGraph(graph));
        globalPlayCanvasGraphs.clear();
        resetBandState(globalBandState);
        this.disableMic();
    },
};
